import { useState, useMemo } from 'react';
import './HeroGrid.css';
import { heroIdToJapaneseName } from '../../data/TranslateJapaneseName';

interface Hero {
  id: number;
  name: string;
  head_url: string | null;
  win_rate: number;
  pick_rate: number;
  ban_rate: number;
  roles: string[];
  lanes: string[];
}

interface HeroGridProps {
  heroes: Hero[];
  pickedHeroIds: ReadonlySet<number>;
  onAddToAlly: (heroId: number) => void;
  onAddToEnemy: (heroId: number) => void;
}

export function HeroGrid({
  heroes,
  pickedHeroIds,
  onAddToAlly,
  onAddToEnemy,
}: HeroGridProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedLane, setSelectedLane] = useState<string>('all');

  // Get unique roles and lanes
  const allRoles = useMemo(() => {
    const roles = new Set<string>();
    heroes.forEach(hero => hero.roles.forEach(role => roles.add(role)));
    return Array.from(roles).sort();
  }, [heroes]);

  const allLanes = useMemo(() => {
    const lanes = new Set<string>();
    heroes.forEach(hero => hero.lanes.forEach(lane => lanes.add(lane)));
    return Array.from(lanes).sort();
  }, [heroes]);

  // Filter and sort heroes
  const filteredHeroes = useMemo(() => {
    let filtered = heroes;

    // Search filter (Japanese or English name)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(hero => {
        const japaneseName = heroIdToJapaneseName[hero.id]?.toLowerCase() || '';
        const englishName = hero.name.toLowerCase();
        return japaneseName.includes(query) || englishName.includes(query);
      });
    }

    // Role filter
    if (selectedRole !== 'all') {
      filtered = filtered.filter(hero => hero.roles.includes(selectedRole));
    }

    // Lane filter
    if (selectedLane !== 'all') {
      filtered = filtered.filter(hero => hero.lanes.includes(selectedLane));
    }

    // Sort by win_rate descending (fixed)
    return filtered.sort((a, b) => b.win_rate - a.win_rate);
  }, [heroes, searchQuery, selectedRole, selectedLane]);

  return (
    <div className="hero-grid-container">
      {/* Search and Filters */}
      <div className="hero-grid-filters">
        <input
          type="text"
          placeholder="ヒーローを検索..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        
        <div className="filter-group">
          <select 
            value={selectedRole} 
            onChange={(e) => setSelectedRole(e.target.value)}
            className="filter-select"
          >
            <option value="all">全ロール</option>
            {allRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>

          <select 
            value={selectedLane} 
            onChange={(e) => setSelectedLane(e.target.value)}
            className="filter-select"
          >
            <option value="all">全レーン</option>
            {allLanes.map(lane => (
              <option key={lane} value={lane}>{lane}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Hero Grid */}
      <div className="hero-grid">
        {filteredHeroes.map(hero => {
          const picked = pickedHeroIds.has(hero.id);
          return (
          <div key={hero.id} className={`hero-card ${picked ? 'hero-picked' : ''}`}>
            <div className="hero-image-container">
              <img 
                src={hero.head_url ?? ''} 
                alt={heroIdToJapaneseName[hero.id] ?? hero.name}
                className="hero-image"
              />
            </div>
            
            <div className="hero-stats">
              <span className="win-rate">{(hero.win_rate * 100).toFixed(1)}%</span>
            </div>
            
            <div className="hero-actions">
              <button 
                type="button"
                className="btn-ally"
                disabled={picked}
                onClick={() => onAddToAlly(hero.id)}
              >
                自
              </button>
              <button 
                type="button"
                className="btn-enemy"
                disabled={picked}
                onClick={() => onAddToEnemy(hero.id)}
              >
                敵
              </button>
            </div>
            
            <div className="hero-name">
              {heroIdToJapaneseName[hero.id] ?? hero.name}
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
}
